import { faInfoCircle, faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import { getBankAccounts, postBankAccount } from 'api/auth';
import { CustomFormControl } from 'components/general/CustomFormControl';
import { IBankAccount, IPostBankAccount } from 'interfaces/IBankAccount';
import { useEffect, useState } from 'react';
import { Row, Button, Col } from 'react-bootstrap';
import { BankAccounts } from './BankAccounts';
import { ICancelContinueModal, IConfirmModal } from 'interfaces/IModal';
import { RequestStatesTypes } from 'types/RequestsStatesTypes';
import { LoadingScreenHandler } from 'components/general/LoadingScreenHandler';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import { formatCardNumber, onlyNumbers, validateAccountNumber } from 'helpers/numbers';
import { ModalHandler } from 'components/general/ModalHandler';
import { AxiosInstance } from 'axios';
import { useModalHandler } from 'hooks/useModalHandler';

/**
 * custom props for the payments component
 */
interface Props {
    /**
     * The axios instance needed for the api calls 
     */
    axiosInstance: AxiosInstance;
    /**
     * call back function to pass the state of the current bank accounts api call state
     */
    sectionLoaded: ( isLoaded: boolean ) => void;
}

/**
 * The upper section of the payment methods component
 */
export const UpperSection = ({ axiosInstance, sectionLoaded }: Props) => {
    const [bankAccountsRequest, setBankAccountsRequest] = useState<RequestStatesTypes>("none");
    const [bankAccounts, setBankAccounts] = useState<IBankAccount[]>();
    const [accountNumber, setAccountNumber] = useState<string>("");
    const [bankName, setBankName] = useState<string>("");
    const [holderName, setHolderName] = useState<string>("");
    const { errorModal, initialModal, modal, setModal, closeModal } = useModalHandler({
        initialMessage: "Favor de revisar la información y confirmar para agregar ésta cuenta/tarjeta:"
    });

    /**
     * Attempts to add the account provided by user
     */
    const addAccount = async (bankAccount: IPostBankAccount) => {
        const updatedBody: IPostBankAccount = {
            ...bankAccount,
            card_number: bankAccount.card_number?.replaceAll('-', "")
        };
        const { data } = await postBankAccount(axiosInstance, updatedBody);
        if (data) {
            closeModal();
            fetchData();
            return;
        }
        const addFailedModal: IConfirmModal = {
            show: true,
            message: "Hubo un problema al intentar agregar esta cuenta\nPor favor intente más tarde",
            onHide() { closeModal() },
            icon: faTimesCircle,
            iconColor: "text-danger",
            title: "Confirmación"
        }
        setModal(addFailedModal);
    }

    /**
     * On add bankAccount function
     */
    const onAddBankAccount = () => {
        const formInformation: IPostBankAccount = {
            account_holder: holderName,
            bank_name: bankName,
            is_default: false,
            status: true,
        };
        const body = validateAccountNumber(accountNumber, formInformation);
        const isFormValid = (holderName.length > 0) && (bankName.length > 0) && body;
        if (!isFormValid) {
            const { onConfirm, ...rest } = initialModal;
            setModal({
                ...rest,
                show: true,
                onHide() { closeModal() },
                message: `${!!body ? "" : "Cuenta o tarjeta inválida\n"}${!!bankName ? "" : "Nombre del banco vacío\n"}${!!holderName ? "" : "Nombre del receptor vacío"}`
            });
            return;
        }
        if (body) {
            const message = initialModal.message +
                `\nCuenta o tarjeta: ${accountNumber}` +
                `\nEntidad bancaria: ${bankName}` +
                `\nNombre del receptor: ${holderName}`;
            const addAccountModal: ICancelContinueModal = {
                ...initialModal,
                show: true,
                message,
                onHide() { closeModal() },
                onConfirm() { addAccount(body) }
            }
            setModal(addAccountModal);
            return;
        }
        setModal(errorModal);
    }

    /**
     * Handles the account number form change
     */
    const handleAccountChange = (value: string) => {
        const account = onlyNumbers(value);
        if (account.length == 16) {
            const cardNumber = formatCardNumber(account);
            cardNumber && setAccountNumber(cardNumber);
            return;
        }
        setAccountNumber(account);
    }

    /**
     * fetch the initial account data
     */
    const fetchData = async () => {
        setBankAccountsRequest("loading");
        const { data } = await getBankAccounts(axiosInstance);
        setBankAccounts(data);
        setBankAccountsRequest(data ? "successful" : "failure");
        sectionLoaded(true);
    }

    /**
     * Use effect that attempts to fetch the initial data of the bank accounts
     */
    useEffect(() => {
        fetchData();
    }, []);

    return (
        <Col>
            <CustomFormControl
                maxLength={24}
                containerCustomClass='mb-3'
                formState={accountNumber}
                onChange={(value) => handleAccountChange(value)}
                labelText="Establecer cuenta o tarjeta para pagos"
                formPlaceholder="Cuenta CLABE o tarjeta de débito"
                tooltipIcon={faInfoCircle}
                tooltipText={
                    <div>
                        La tarjeta se compone de 16 digitos <br />
                        La cuenta CLABE se compone de 18 digitos <br />
                    </div>
                }
                isReadOnly={false}
                colSpan={12}
            />
            <Row xs={12} className='mb-3'>
                <CustomFormControl
                    formState={bankName}
                    onChange={(value) => setBankName(value)}
                    labelText="Entidad bancaria"
                    formPlaceholder="Entidad bancaria"
                    tooltipIcon={faInfoCircle}
                    tooltipText={
                        <div>
                            La entidad bancaria es el banco de<br />
                            donde es proveniente la cuenta ingresada,<br />
                            Ej. BBVA, Santander, Scotiabank, etc.<br />
                        </div>
                    }
                    tooltipPlacement="top"
                    isReadOnly={false}
                    colSpan={4}
                />
                <CustomFormControl
                    formState={holderName}
                    onChange={(value) => setHolderName(value)}
                    labelText="Nombre de receptor:"
                    formPlaceholder="Nombre del receptor"
                    tooltipIcon={faInfoCircle}
                    tooltipText={"El nombre del receptor es el nombre completo \n del dueño de la cuenta / tarjeta"}
                    tooltipPlacement="top"
                    isReadOnly={false}
                    colSpan={8}
                />
            </Row>
            <Row className='justify-content-end mb-3'>
                <Button
                    variant="primary"
                    type="button"
                    onClick={onAddBankAccount}
                    className=" fw-bold align-item-center text-center me-2"
                    style={{ width: "fit-content" }}
                >
                    Añadir
                </Button>
            </Row>
            <LoadingScreenHandler
                loadingText="Actualizando las cuentas bancarias."
                requestState={bankAccountsRequest}
                failureComponent={
                    <div className='text-center'>
                        <WifiOffIcon sx={{ fontSize: 100 }} />
                        <h4><strong>No pudimos obtener los datos del servidor</strong></h4>
                    </div>
                }
                successComponent={
                    <>
                        {bankAccounts &&
                            <BankAccounts
                                axiosInstance={axiosInstance}
                                bankAccounts={bankAccounts}
                                updateAccounts={()  => fetchData()}
                            />
                        }
                    </>
                }
            />
            <ModalHandler modal={modal} />
        </Col>
    )
}
